'use strict';

import { isTrackingDebugEnabled, trackingTrace } from '../../../communication/analytics/TrackingDebug';
import { readFrameResource } from './Readium/frameAccess.js';
import { READING_EDGE_EPSILON } from './Readium/readingAnchor.js';

const DEFAULT_CONFIRM_DELAY_MS = 3000;
const EPUB_OPS_NAMESPACE = 'http://www.idpf.org/2007/ops';
/**
 * How long after the last position change the settle re-check runs. Position
 * events can fire mid page-turn animation (~300ms), when client rects are
 * still in flight; one extra check after the movement stops corrects
 * whatever those snapshots got wrong.
 */
const SETTLE_DELAY_MS = 450;

/**
 * Id of the marker Farfalla's EpubPagebreakInjector writes to open printed
 * page N. Lives beside the readers that parse marker ids, so writing one and
 * parsing one stay on the same format.
 *
 * @param {number} page
 * @returns {string}
 */
export function injectedPagebreakMarkerId(page) {
    return `pla-pb-${page}`;
}

/**
 * Whether an element is a printed-page marker, accepting both spec variants:
 * the ARIA role and the EPUB 3 `epub:type` vocabulary. Publishers frequently
 * emit only `epub:type` (the ARIA role is a recommendation, not a
 * requirement), and the backend counts those books as marker-bearing — the
 * tracker must see the same markers or the emission branch starves.
 *
 * `epub:type` is a namespaced attribute and EPUB chapters parse as XHTML
 * (XML documents), where CSS attribute selectors cannot address foreign
 * namespaces — `[epub\:type]` silently matches nothing there. DOM lookups
 * are reliable in both parse modes: getAttribute matches the literal
 * qualified name (the near-universal `epub:` prefix) and getAttributeNS
 * covers documents that bind the vocabulary to a different prefix.
 */
export function isPagebreakMarker(element) {
    if (element.getAttribute('role') === 'doc-pagebreak') return true;

    const epubType = element.getAttribute('epub:type') ?? element.getAttributeNS?.(EPUB_OPS_NAMESPACE, 'type');
    return typeof epubType === 'string' && epubType.split(/\s+/).includes('pagebreak');
}

/**
 * Every printed-page marker in a chapter document, in DOM (reading) order.
 *
 * A full element walk rather than a query because of the namespace problem above; one pass
 * keeps mixed-variant chapters in reading order. Exported so the dev-only pagebreak overlay
 * marks exactly the elements the tracker counts — two walks that could disagree about what
 * a marker is would make the overlay lie about what it is visualising.
 *
 * @param {Document} doc
 * @returns {Element[]}
 */
export function collectPagebreakMarkers(doc) {
    return Array.from(doc.querySelectorAll('*')).filter(isPagebreakMarker);
}

/**
 * Extracts the printed page number from a pagebreak element. Prefers the
 * accessible `aria-label`, then `title`, then the trailing digit run of the
 * id (mirrors the analytics parsing in TrackingEventsListener, so the inner
 * `1` of `chapter1-pb-3` is ignored). Returns null when no digits are found.
 *
 * Exported so every consumer resolves a marker to the same number: the
 * tracker's readout page here, and the fixed-layout spread feed in the
 * viewer — two parsers that could disagree would show the reader one page
 * and hand the reader plugin another.
 *
 * @param {Element} el
 * @returns {number | null}
 */
export function pagebreakPageNumber(el) {
    const raw = el.getAttribute('aria-label') ?? el.getAttribute('title') ?? el.id ?? '';
    const matches = String(raw).match(/\d+/g);
    if (!matches || matches.length === 0) return null;
    return Number(matches[matches.length - 1]);
}

/** Whether a client rect touches a viewport of the given dimensions. */
function rectIntersectsViewport(rect, viewportWidth, viewportHeight) {
    return rect.right > 0 && rect.left < viewportWidth && rect.bottom >= 0 && rect.top <= viewportHeight;
}

/**
 * Whether a marker's start point lies entirely past the viewport's exit
 * edge — content from that point on has not scrolled/paged in yet. Columns
 * advance along x, scroll mode along y; a point past either edge is ahead
 * of the reading window in both layouts.
 */
function startsAfterViewport(rect, viewportWidth, viewportHeight) {
    return rect.left >= viewportWidth || rect.top >= viewportHeight;
}

/**
 * Whether a marker's start point already left through the viewport's entry
 * edge — content up to that point has fully paged out.
 */
function endsBeforeViewport(rect) {
    return rect.right <= 0 || rect.bottom <= 0;
}

/**
 * Detects printed-page boundaries in EPUB iframes and reports two signals:
 * a page becoming visible (page turn) and a page staying visible for a
 * configurable delay (confirmed page view).
 *
 * A marker is the START of a printed page: page N is the content interval
 * between marker N and marker N+1 (or the end of the chapter). Visibility
 * is therefore decided per PAGE, not per marker element — a page whose
 * marker scrolled out but whose tail is still on screen is still being
 * read, and several pages can legitimately be on screen at once.
 *
 * Uses manual getBoundingClientRect() checks instead of IntersectionObserver
 * because Readium's paginated mode uses CSS columns + transforms that do
 * not trigger IntersectionObserver callbacks on page changes.
 */
export class PagebreakTracker {
    /**
     * @param {Function} onPagebreakConfirmed - Called with pagebreak info
     *   after the page has remained on screen for the configured delay.
     *   Receives: { title: string, id: string }
     * @param {Object} [options={}]
     * @param {number} [options.confirmDelayMs] - Milliseconds the page
     *   must remain on screen before confirming. Defaults to 3000.
     * @param {Function} [options.onPagebreaksEntered] - Called with the batch
     *   of pages (DOM order) that just transitioned not-visible → visible:
     *   `[{ title, id }, ...]`. The very first non-empty batch after (re)start
     *   is swallowed as the opening baseline — opening a book is not entering
     *   a page.
     * @param {Function} [options.onCurrentPageChanged] - Called with the page
     *   at the reading edge (`{ number, id, title }`) whenever a visibility
     *   check changes it — including the delayed settle pass, so a readout
     *   painted mid page-turn corrects itself once the geometry settles.
     *   Null only before any page has resolved and on teardown: once a page is
     *   known, a check that resolves none keeps it rather than reporting null
     *   (see `_setCurrentPage`). Staleness within one scale beats a silent
     *   switch to a Readium position with nothing marking the change of units.
     * @param {Function} [options.getReadingContext] - Returns the live
     *   `{ scroll, edgeStart }` layout context: which axis the reading edge
     *   lives on and how far the content is inset from the viewport border.
     *   A provider (not a value) because layout and margins change at runtime.
     */
    constructor(
        onPagebreakConfirmed,
        { confirmDelayMs, onPagebreaksEntered, onCurrentPageChanged, getReadingContext } = {}
    ) {
        this._onPagebreakConfirmed = onPagebreakConfirmed;
        this._onPagebreaksEntered = onPagebreaksEntered ?? null;
        this._onCurrentPageChanged = onCurrentPageChanged ?? null;
        this._getReadingContext = getReadingContext ?? (() => ({ scroll: false, edgeStart: 0 }));
        this._confirmDelayMs = confirmDelayMs ?? DEFAULT_CONFIRM_DELAY_MS;
        /**
         * Pending confirmation timers, keyed like _confirmed.
         * @type {Map<string | Element, number>}
         */
        this._timers = new Map();
        /** @type {{ frameWindow: Window, pagebreaks: Element[] }[]} */
        this._frames = [];
        /** @type {Set<number>} */
        this._retryIntervals = new Set();
        /**
         * Keys of the pages visible on the last check. Keyed by marker id
         * (string), not element: Readium recycles iframes (tab return,
         * chapter reload), and the same page must not read as a fresh entry
         * just because its DOM node was replaced. Markers without an id
         * can't be tracked for entry (no stable key) — they still confirm
         * page-views via _timers.
         * @type {Set<string | Element>}
         */
        this._visiblePages = new Set();
        /** @type {boolean} */
        this._baselineDone = false;
        /**
         * Pages already confirmed as viewed AND still on screen. Without
         * this, the pending timer is the tracker's only memory and it dies on
         * confirmation — the very next visibility check re-arms it and the
         * same page confirms again every delay-worth of dwelling (scroll
         * mode re-checks on every scroll event). Entries drop when the page
         * leaves the viewport, so returning to a page after leaving it counts
         * as a fresh read.
         * @type {Set<string | Element>}
         */
        this._confirmed = new Set();
        /** @type {number | null} */
        this._settleTimerId = null;
        /**
         * The page at the reading edge as of the last settled check, or null
         * when no numbered page is current. Kept through empty mid-animation
         * snapshots, like _visiblePages.
         * @type {{ number: number, id: string | null, title: string | null } | null}
         */
        this._currentPage = null;
    }

    /**
     * Stable identity for a page across iframe recycling: the marker id
     * survives a document reload; anonymous markers fall back to node
     * identity. Scoped to the chapter that carries it, because publishers
     * number their pagebreak ids per file and reflowable pagination keeps
     * neighbour chapters attached — so one id names two printed pages.
     * @private
     */
    _pageKey(element, resource = '') {
        if (!element.id) return element;
        return resource ? `${resource}#${element.id}` : element.id;
    }

    /**
     * Registers an iframe for pagebreak tracking.
     * Guards against placeholder bodies and retries until real content is ready.
     *
     * @param {Window} frameWindow
     */
    observeFrame(frameWindow) {
        const doc = frameWindow.document;
        if (!doc?.body) return;

        if (doc.body.textContent?.length <= 50) {
            let attempts = 0;
            const retryId = setInterval(() => {
                attempts++;
                if (attempts > 20) {
                    clearInterval(retryId);
                    this._retryIntervals.delete(retryId);
                    return;
                }
                try {
                    if (doc.body?.textContent?.length > 50) {
                        clearInterval(retryId);
                        this._retryIntervals.delete(retryId);
                        this._registerFrame(frameWindow);
                    }
                } catch (_e) {
                    clearInterval(retryId);
                    this._retryIntervals.delete(retryId);
                }
            }, 500);
            this._retryIntervals.add(retryId);
            return;
        }

        this._registerFrame(frameWindow);
    }

    /** @private */
    _registerFrame(frameWindow) {
        const doc = frameWindow.document;

        this._frames = this._frames.filter(f => f.frameWindow.document !== doc);

        // Single walk in DOM order, once per chapter load.
        const pagebreaks = collectPagebreakMarkers(doc);

        if (isTrackingDebugEnabled()) {
            let href = null;
            try {
                href = frameWindow.location?.pathname ?? null;
            } catch (_e) {
                // Foreign frame without a readable location still traces the count.
            }
            trackingTrace('pagebreaks:frame-registered', { href, markers: pagebreaks.length });
        }

        if (pagebreaks.length === 0) return;

        this._frames.push({ frameWindow, pagebreaks, resource: readFrameResource(frameWindow) });
        this.checkVisibility('frame-registered');
    }

    /**
     * The printed page number at the reading edge right now, or null when no
     * numbered page is current (before the first marker, or non-numeric
     * labels only) so the caller can fall back to the Readium position.
     *
     * Synchronous pull counterpart of `onCurrentPageChanged`: a position
     * change can ask immediately (possibly against mid-animation geometry),
     * and the settle-driven callback corrects the consumer afterwards. Both
     * run the same walk over the same registered frames — a single source of
     * truth with the analytics signals.
     *
     * Once a printed page has been established, a screen the walk cannot
     * resolve means "unknown", not "no printed page", so the last settled
     * page answers instead. Reading never leaves the printed pagination: a
     * screen showing no resolvable marker is still somewhere inside the last
     * page. The guard used to key on an EMPTY measurement, which missed the
     * common case — a marker that sits past the reading band's trailing edge
     * is skipped by both edge tests (neither crossed nor beginning on screen)
     * while interval semantics still count its page as visible, so
     * `visible.size` was non-zero, the guard never fired, and the pull
     * answered null. The consumer then swapped in a Readium position, and the
     * settle pass stayed silent whenever the turn ended on the page it
     * started from (`_setCurrentPage` compares against the tracker's own
     * state, which never changed), leaving that foreign number on screen
     * until the next printed page.
     *
     * Null therefore means only "no printed page has ever been current" —
     * front matter before the first marker, or a book without markers.
     *
     * @returns {number|null}
     */
    getCurrentPageNumber() {
        const { currentPage } = this._computeVisibleState();

        return (currentPage ?? this._currentPage)?.number ?? null;
    }

    /**
     * The numbered printed pages on screen right now, ascending.
     *
     * Same walk as the readout, but every visible page instead of the one at
     * the reading edge: a screen can show several pagebreaks, and whatever is
     * anchored to any of them is on screen.
     * @returns {number[]}
     */
    getVisiblePageNumbers() {
        const numbers = new Set();
        for (const element of this._computeVisiblePages().values()) {
            const number = this._pageNumberOf(element);
            if (number !== null) numbers.add(number);
        }
        return [...numbers].sort((a, b) => a - b);
    }

    /** @private Delegates to the module-level parser (see pagebreakPageNumber). */
    _pageNumberOf(el) {
        return pagebreakPageNumber(el);
    }

    /**
     * A registered window that still answers for its document and viewport.
     * A destroyed or recycled iframe loses its body; on Firefox a detached
     * iframe keeps its document reachable but throws NS_ERROR_UNEXPECTED on
     * `innerWidth` (no docshell), for good.
     * @private
     * @param {Window} frameWindow
     * @returns {boolean}
     */
    _isFrameAlive(frameWindow) {
        try {
            return frameWindow.document?.body !== null && typeof frameWindow.innerWidth === 'number';
        } catch (_e) {
            return false;
        }
    }

    /**
     * Whether the chapter iframe itself is part of what the reader shows.
     * Readium keeps neighboring chapters preloaded in a frame pool; a pooled
     * frame's markers measure "visible" against their own viewport forever,
     * so page visibility has to compose with the frame element's own
     * geometry in the reader window. Frames without a reachable element
     * (tests, detached documents) are assumed on screen.
     * @private
     * @param {Window} frameWindow
     */
    _isFrameOnScreen(frameWindow) {
        let frameElement;
        try {
            frameElement = frameWindow.frameElement;
        } catch (_e) {
            return true;
        }
        if (!frameElement) return true;

        const rect = frameElement.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) return false;
        if (!rectIntersectsViewport(rect, window.innerWidth, window.innerHeight)) return false;

        // Pools hide frames by position most of the time, but visibility and
        // display are also used across Readium versions. Opacity is NOT
        // checked: the page-turn fade animates it on the CURRENT frame, and
        // treating a mid-fade frame as gone would cancel its timers.
        const style = window.getComputedStyle(frameElement);
        return style.visibility !== 'hidden' && style.display !== 'none';
    }

    /**
     * The set of pages currently on screen, as a Map from page key to its
     * marker element. Page i of a frame spans from marker i to marker i+1
     * (or the end of the chapter): it is on screen while its interval
     * touches the frame viewport — its own marker may already be out.
     * @private
     * @returns {Map<string | Element, Element>}
     */
    _computeVisiblePages() {
        return this._computeVisibleState().visible;
    }

    /**
     * One walk over the registered on-screen frames producing both signals:
     * the set of visible pages (interval semantics, for analytics) and the
     * page at the reading edge (for the UI readout). Sharing the walk is the
     * point — the readout and the analytics events cannot disagree about
     * which markers exist or which frames count.
     *
     * The current page is decided against the reading edge from
     * `getReadingContext`:
     *   - paginated: the first numbered page that BEGINS on the current
     *     screen (its marker overlaps the visible band), else the page in
     *     progress — the last numbered marker that crossed off the leading
     *     edge. A long printed page spanning several screens keeps its
     *     number on the marker-less screens instead of dropping to null.
     *   - scroll: the last numbered marker scrolled above the reading line,
     *     else the first numbered marker on screen (before any was passed).
     *
     * The walk itself is direction-agnostic: the DOM is in reading order
     * whatever the visual direction, so document order picks the leading page
     * for LTR, RTL and spreads alike. The paginated geometry underneath is
     * not — it reads the leading edge as the left one, so an RTL publication,
     * whose leading edge is on the right, resolves the trailing page or none
     * at all. Fixing that means carrying the reading direction through
     * `getReadingContext` and mirroring the edge tests below; the RTL case in
     * the suite documents the current limitation rather than covering it.
     *
     * @private
     * @returns {{
     *   visible: Map<string | Element, Element>,
     *   currentPage: { number: number, id: string | null, title: string | null } | null,
     * }}
     */
    _computeVisibleState() {
        const visible = new Map();
        const { scroll, edgeStart } = this._getReadingContext();
        // The page in progress: last numbered marker crossed off the leading
        // edge (scroll: scrolled above the reading line).
        let crossedPage = null;
        // The first numbered page that begins on the current screen, in
        // document (reading) order.
        let firstOnScreenPage = null;

        // Every walk, pulled or scheduled, drops the frames that stopped
        // answering (destroyed or recycled iframes), so all callers read one set.
        this._frames = this._frames.filter(({ frameWindow }) => this._isFrameAlive(frameWindow));

        for (const { frameWindow, pagebreaks, resource } of this._frames) {
            if (!this._isFrameOnScreen(frameWindow)) continue;

            const viewportWidth = frameWindow.innerWidth;
            const viewportHeight = frameWindow.innerHeight;

            for (let i = 0; i < pagebreaks.length; i++) {
                const element = pagebreaks[i];
                const rect = element.getBoundingClientRect();

                // Interval semantics: page i spans marker i → marker i+1 and
                // is visible while that interval touches the frame viewport.
                if (!startsAfterViewport(rect, viewportWidth, viewportHeight)) {
                    const next = pagebreaks[i + 1];
                    if (!next || !endsBeforeViewport(next.getBoundingClientRect())) {
                        visible.set(this._pageKey(element, resource), element);
                    }
                }

                // Reading-edge readout: numbered markers only — an unnumbered
                // marker cannot be shown and must not shadow a numbered one.
                const number = this._pageNumberOf(element);
                if (number === null) continue;
                const page = { number, id: element.id || null, title: element.getAttribute('title') };

                if (scroll) {
                    if (rect.top <= edgeStart + READING_EDGE_EPSILON) {
                        crossedPage = page;
                    } else if (rect.top < viewportHeight - edgeStart && firstOnScreenPage === null) {
                        firstOnScreenPage = page;
                    }
                } else {
                    // The marker begins on screen when its rect overlaps the
                    // visible band [edgeStart, width - edgeStart]. The left
                    // test counts a marker that extends past the gutter OR
                    // starts at/after it — so a zero-width marker opening the
                    // column (an empty inline pagebreak at left === right ===
                    // edgeStart) is on screen, while a previous-column marker
                    // (entirely left of the gutter) is not: that one is the
                    // crossed (in-progress) candidate.
                    const pastGutter =
                        rect.right > edgeStart + READING_EDGE_EPSILON || rect.left >= edgeStart - READING_EDGE_EPSILON;
                    if (!pastGutter) {
                        crossedPage = page;
                    } else if (
                        rect.left < viewportWidth - edgeStart - READING_EDGE_EPSILON &&
                        firstOnScreenPage === null
                    ) {
                        firstOnScreenPage = page;
                    }
                }
            }
        }

        // Scroll reads by a line, not a band: the page at the top of the
        // viewport is the last one scrolled past. Paginated shows whole
        // pages: the first page beginning here wins, and the crossed page
        // only answers for marker-less screens (a long page's overflow).
        const currentPage = scroll ? (crossedPage ?? firstOnScreenPage) : (firstOnScreenPage ?? crossedPage);
        return { visible, currentPage };
    }

    /**
     * Checks which printed pages are currently on screen and manages
     * confirmation timers accordingly. Call on every position/page change.
     *
     * A check that finds no pages at all keeps the previous state instead of
     * cancelling everything: snapshots taken mid page-turn can see an empty
     * viewport, and a chapter with content before its first marker (or with
     * no markers) still belongs to the last known page — the settle re-check
     * and later position changes make the real determination.
     *
     * @param {string} [trigger] - What prompted the check; recorded in debug
     *   traces, and 'settle' marks the delayed post-animation pass (which
     *   must not reschedule itself).
     */
    checkVisibility(trigger = 'position-changed') {
        this._scheduleSettleCheck(trigger);

        const debug = isTrackingDebugEnabled();
        const { visible: nowVisible, currentPage } = this._computeVisibleState();

        if (nowVisible.size === 0 && this._visiblePages.size > 0) {
            if (debug) {
                trackingTrace('pagebreaks:check', {
                    trigger,
                    kept: [...this._visiblePages].map(String),
                    reason: 'empty-snapshot-keeps-state',
                });
            }
            return;
        }

        const previousVisible = this._visiblePages;
        const armedKeys = debug ? [] : null;
        const cancelledKeys = debug ? [] : null;

        this._notifyEnteredPagebreaks(nowVisible, previousVisible);

        // Confirmations only hold while the page stays on screen: rebuilding
        // the set against the current visibility drops the ones that left, so
        // a genuine return to the page re-confirms after the delay.
        const stillConfirmed = new Set();
        for (const key of nowVisible.keys()) {
            if (this._confirmed.has(key)) {
                stillConfirmed.add(key);
            }
        }
        this._confirmed = stillConfirmed;

        for (const [key, element] of nowVisible) {
            if (!this._timers.has(key) && !this._confirmed.has(key)) {
                this._armConfirmationTimer(key, element);
                armedKeys?.push(String(key));
            }
        }

        for (const [key, timerId] of this._timers) {
            if (!nowVisible.has(key)) {
                clearTimeout(timerId);
                this._timers.delete(key);
                cancelledKeys?.push(String(key));
            }
        }

        this._visiblePages = new Set(nowVisible.keys());
        this._setCurrentPage(currentPage);

        if (debug) {
            const visibleKeys = [...nowVisible.keys()].map(String);
            trackingTrace('pagebreaks:check', {
                trigger,
                current: currentPage?.number ?? null,
                visible: visibleKeys,
                entered: visibleKeys.filter(key => !previousVisible.has(key)),
                exited: [...previousVisible].filter(key => !nowVisible.has(key)).map(String),
                armed: armedKeys,
                cancelled: cancelledKeys,
                rects: [...nowVisible.values()].map(el => {
                    const rect = el.getBoundingClientRect();
                    return { id: el.id || '(anon)', left: Math.round(rect.left), top: Math.round(rect.top) };
                }),
            });
        }
    }

    /**
     * Caches the page at the reading edge and notifies the consumer when it
     * actually changed. Unlike the entry batches there is no opening
     * baseline: the readout wants the opening page too.
     *
     * A check that resolves no page keeps the last one rather than reporting
     * null, matching the pull (see `getCurrentPageNumber`): the reader is
     * still inside the last printed page, and emitting null would make the
     * consumer paint a number from a different scale. Only teardown clears
     * the page back to null.
     * @private
     * @param {{ number: number, id: string | null, title: string | null } | null} page
     */
    _setCurrentPage(page) {
        // The whole behavioural change lives here: a check that resolved no
        // page used to fall through to the comparison below, where a known
        // page never equals null, so it cleared the page and notified null.
        // The comparison itself is unchanged.
        if (page == null && this._currentPage) return;

        const previous = this._currentPage;
        if (previous?.number === page?.number && previous?.id === page?.id) return;

        this._currentPage = page;
        this._onCurrentPageChanged?.(page);
    }

    /**
     * The confirmation timer re-measures at expiry: a timer armed off an
     * in-flight snapshot (page-turn animation) may belong to a page that
     * never really settled on screen, and confirming it would count a view
     * the reader never had. A suppressed page re-arms on the next check that
     * genuinely sees it.
     * @private
     */
    _armConfirmationTimer(key, element) {
        const timerId = setTimeout(() => {
            this._timers.delete(key);

            const pagesNow = this._computeVisiblePages();
            // An empty measurement means "state unknown" (mid-transition),
            // not "page gone": fall back to the last settled state.
            const onScreen = pagesNow.size > 0 ? pagesNow.has(key) : this._visiblePages.has(key);
            if (!onScreen) {
                trackingTrace('pagebreaks:view-suppressed', {
                    id: element.id || '(anon)',
                    reason: 'off-screen-at-expiry',
                });
                return;
            }

            this._confirmed.add(key);
            if (isTrackingDebugEnabled()) {
                trackingTrace('pagebreaks:view-confirmed', {
                    id: element.id || '(anon)',
                    title: element.getAttribute('title'),
                    stillVisible: true,
                });
            }
            this._confirmPagebreak(element);
        }, this._confirmDelayMs);
        this._timers.set(key, timerId);
    }

    /**
     * One extra check after movement stops. Position changes arrive
     * mid-animation, when rects lie in both directions (a crossed page still
     * "in", the destination still "out"); the settle pass runs against the
     * final geometry and corrects timers and entries either way.
     * @private
     */
    _scheduleSettleCheck(trigger) {
        if (trigger === 'settle') return;

        if (this._settleTimerId !== null) {
            clearTimeout(this._settleTimerId);
        }
        this._settleTimerId = setTimeout(() => {
            this._settleTimerId = null;
            this.checkVisibility('settle');
        }, SETTLE_DELAY_MS);
    }

    /**
     * Diffs the visible-page keys against the previous check and reports the
     * batch of fresh entries (DOM order) to `onPagebreaksEntered`. Re-checks
     * with no visibility change (tab return, re-render, position re-emits)
     * produce no transitions and stay silent — that is the whole point.
     * @private
     * @param {Map<string | Element, Element>} nowVisible
     * @param {Set<string | Element>} previousVisible
     */
    _notifyEnteredPagebreaks(nowVisible, previousVisible) {
        if (!this._onPagebreaksEntered) return;

        const entered = [];
        for (const [key, element] of nowVisible) {
            // Anonymous pages have no stable identity across recycles: no entry.
            if (typeof key !== 'string') continue;
            if (!previousVisible.has(key)) {
                // The key is chapter-scoped for uniqueness; what analytics
                // names is the marker's own id.
                entered.push({ title: element.getAttribute('title'), id: element.id });
            }
        }

        if (entered.length === 0) return;

        if (!this._baselineDone) {
            // Opening the book always lands on a marker (platform injection is
            // page-open: chapters begin with a span, covers carry pla-pb-0),
            // so the first batch is the starting position, not navigation.
            this._baselineDone = true;
            trackingTrace('pagebreaks:baseline-swallowed', { ids: entered.map(e => e.id) });
            return;
        }

        trackingTrace('pagebreaks:entered-batch', { ids: entered.map(e => e.id) });
        this._onPagebreaksEntered(entered);
    }

    /** @private */
    _confirmPagebreak(element) {
        if (!this._onPagebreakConfirmed) return;
        this._onPagebreakConfirmed({
            title: element.getAttribute('title'),
            id: element.id,
        });
    }

    disconnectAll() {
        this._retryIntervals.forEach(id => clearInterval(id));
        this._retryIntervals.clear();
        this._timers.forEach(timerId => clearTimeout(timerId));
        this._timers.clear();
        if (this._settleTimerId !== null) {
            clearTimeout(this._settleTimerId);
            this._settleTimerId = null;
        }
        this._frames = [];
        this._visiblePages = new Set();
        this._baselineDone = false;
        this._confirmed = new Set();
        this._currentPage = null;
    }

    destroy() {
        this.disconnectAll();
        this._onPagebreakConfirmed = null;
        this._onPagebreaksEntered = null;
        this._onCurrentPageChanged = null;
    }
}
